import React, { useState } from "react";
import { Button, Input, message } from "antd";

import { saveEstimateDefaults } from "../TreatmentPlans/component/shared/estimateClaimsApi";

/**
 * Collect the office's CDA / iTrans number, once, from wherever it is first missing.
 *
 * The number identifies the PRACTICE to a carrier and never changes, so it is
 * stored on the office's insurance profile rather than per request. An ESTIMATE
 * already captures it as a side effect — every claim row carries the field, and
 * the server keeps the first non-blank one it sees. A BREAKDOWN has no claim rows,
 * so it has never had anywhere to ask, and an office whose only requests are
 * breakdowns can run indefinitely without one on file. Then their first estimate
 * asks for a number nobody has to hand.
 *
 * So: ask here, but only when there is nothing on file. Once saved this renders
 * nothing, on this request and every one after it.
 *
 * Deliberately NOT a gate. A breakdown does not need the number to go out, and
 * blocking a verification on a field it does not use would trade one problem for
 * a worse one. Skipping costs nothing beyond being asked again next time.
 */
export default function OfficeCdaPrompt({ patientId, value, onSaved }) {
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  // Already on file — nothing to ask.
  if (!patientId || (value || "").trim()) return null;

  async function save() {
    const number = draft.trim();
    if (!number) return;
    setSaving(true);
    try {
      const res = await saveEstimateDefaults(patientId, { cda_itrans_number: number });
      if (res.ok) {
        message.success("Saved to your practice — we won't ask again.");
        onSaved(number);
      } else {
        message.error(
          (res.json && res.json.detail) || "Couldn't save that number. Please try again.",
        );
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="ir-cda">
      <div className="ir-cda-head">Your practice&rsquo;s CDA / iTrans #</div>
      <div className="ir-cda-sub">
        We don&rsquo;t have one on file. It&rsquo;s the same number on every claim you
        send, so adding it once saves typing it on every estimate later. Optional
        &mdash; this request goes out either way.
      </div>
      <div className="ir-cda-row">
        <Input
          value={draft}
          placeholder="e.g. 123456"
          onChange={function (e) { setDraft(e.target.value); }}
          onPressEnter={save}
        />
        <Button type="primary" loading={saving} disabled={!draft.trim()} onClick={save}>
          Save
        </Button>
      </div>
    </div>
  );
}
